/**
 * Privacy-compliant referrer tracking utilities
 * Categorizes traffic sources without storing sensitive information
 */

// Categorized referrer types for privacy-compliant analytics
export type ReferrerCategory = 
  | 'direct' 
  | 'search_engine' 
  | 'social_media' 
  | 'academic' 
  | 'news_blog' 
  | 'other_website'
  | 'email'
  | 'bot'
  | 'developer_tool'
  | 'unknown';

interface ReferrerInfo {
  category: ReferrerCategory;
  source?: string; // Only store general source name, not full URL
  isInternal: boolean;
  botType?: string; // 'search_bot' | 'social_bot' | 'seo_bot' | 'other_bot'
}

// Privacy-safe domain patterns for categorization
const SEARCH_ENGINES = [
  'google.com', 'bing.com', 'duckduckgo.com', 'yahoo.com', 
  'yandex.com', 'baidu.com', 'ecosia.org'
];

const SOCIAL_MEDIA = [
  'twitter.com', 'x.com', 'linkedin.com', 'facebook.com', 
  'reddit.com', 'discord.com', 'telegram.org'
];

const ACADEMIC_DOMAINS = [
  '.edu', '.ac.uk', '.ac.', 'researchgate.net', 'scholar.google.com',
  'arxiv.org', 'pubmed.ncbi.nlm.nih.gov', 'ieee.org', 'acm.org'
];

const NEWS_BLOG = [
  'medium.com', 'substack.com', 'wordpress.com', 'blogger.com',
  'news.ycombinator.com', 'hackernews.com'
];

const EMAIL_INDICATORS = [
  'mail.google.com', 'outlook.com', 'outlook.live.com', 
  'mail.yahoo.com', 't.co' // Twitter's URL shortener often used in emails
];

// Development tools detection (IDEs, embedded browsers)
const DEV_TOOLS = [
  'cursor', 'vscode', 'electron', 'postman', 'insomnia'
];

// Bot detection patterns
const BOT_PATTERNS = {
  search_bots: [
    'googlebot', 'bingbot', 'slurp', 'duckduckbot', 
    'baiduspider', 'yandexbot', 'sogou', 'exabot',
    'google-inspectiontool' // Google Search Console verification
  ],
  social_bots: [
    'facebookexternalhit', 'twitterbot', 'linkedinbot',
    'whatsapp', 'telegrambot', 'discordbot', 'slackbot',
    'meta-externalagent', 'facebot'
  ],
  seo_monitoring: [
    'ahrefsbot', 'semrushbot', 'mj12bot', 'dotbot',
    'screaming frog', 'serpstatbot'
  ],
  other_bots: [
    'adsbot', 'crawler', 'spider', 'scraper', 'bot',
    'ev-crawler', 'petalbot', 'applebot', 'bytespider'
  ]
};

/**
 * Detect if a user agent is a bot and categorize it
 */
const detectBot = (userAgent: string): { 
  isBot: boolean; 
  botType?: string; 
  botName?: string 
} => {
  const lowerUA = userAgent.toLowerCase();
  
  // Check search engine bots
  for (const bot of BOT_PATTERNS.search_bots) {
    if (lowerUA.includes(bot)) {
      let botName = bot.replace('bot', '').replace('spider', '');
      if (botName === 'google') botName = 'Googlebot';
      else if (botName === 'bing') botName = 'Bingbot';
      else if (botName === 'yandex') botName = 'Yandexbot';
      else if (botName === 'baidu') botName = 'Baiduspider';
      else botName = botName.charAt(0).toUpperCase() + botName.slice(1) + 'bot';
      
      return { isBot: true, botType: 'search_bot', botName };
    }
  }
  
  // Check social media bots
  for (const bot of BOT_PATTERNS.social_bots) {
    if (lowerUA.includes(bot)) {
      let botName = bot.replace('bot', '').replace('externalhit', '');
      if (botName === 'facebook' || botName === 'face' || botName === 'meta-external') 
        botName = 'Facebook Bot';
      else if (botName === 'twitter') botName = 'Twitter Bot';
      else if (botName === 'linkedin') botName = 'LinkedIn Bot';
      else if (botName === 'discord') botName = 'Discord Bot';
      else if (botName === 'telegram') botName = 'Telegram Bot';
      else if (botName === 'slack') botName = 'Slack Bot';
      else botName = botName.charAt(0).toUpperCase() + botName.slice(1) + ' Bot';
      
      return { isBot: true, botType: 'social_bot', botName };
    }
  }
  
  // Check SEO monitoring bots
  for (const bot of BOT_PATTERNS.seo_monitoring) {
    if (lowerUA.includes(bot)) {
      let botName = bot.replace('bot', '');
      if (botName === 'ahrefs') botName = 'Ahrefs Bot';
      else if (botName === 'semrush') botName = 'SEMrush Bot';
      else botName = botName.charAt(0).toUpperCase() + botName.slice(1);
      
      return { isBot: true, botType: 'seo_bot', botName };
    }
  }
  
  // Check other bots with specific naming
  for (const bot of BOT_PATTERNS.other_bots) {
    if (lowerUA.includes(bot)) {
      let botName = 'Other Bot';
      
      // Provide specific names for known bots
      if (bot === 'applebot') botName = 'Apple Bot';
      else if (bot === 'petalbot') botName = 'Petal Bot';
      else if (bot === 'adsbot') botName = 'Ads Bot';
      else if (bot === 'ev-crawler') botName = 'EV Crawler';
      else if (bot === 'bytespider') botName = 'Bytespider';
      else if (bot === 'crawler' && lowerUA.includes('curl')) botName = 'cURL';
      else if (bot === 'crawler' && lowerUA.includes('wget')) botName = 'wget';
      else if (bot === 'crawler' && lowerUA.includes('python')) botName = 'Python Crawler';
      else if (bot === 'crawler' && lowerUA.includes('java')) botName = 'Java Crawler';
      else if (bot === 'scraper') botName = 'Web Scraper';
      else if (bot === 'spider') botName = 'Web Spider';
      // Extract custom bot name from common patterns like "CustomBot/1.0"
      else {
        const botMatch = userAgent.match(/([A-Za-z0-9-]+)[Bb]ot[\s/]/i);
        if (botMatch) {
          botName = botMatch[1].charAt(0).toUpperCase() + botMatch[1].slice(1) + 'bot';
        }
      }
      
      return { isBot: true, botType: 'other_bot', botName };
    }
  }
  
  return { isBot: false };
};

/**
 * Extract domain from URL without storing the full URL
 */
const extractDomain = (url: string): string | null => {
  try {
    const domain = new URL(url).hostname.toLowerCase();
    return domain;
  } catch {
    return null;
  }
};

/**
 * Check if referrer is from the same site (internal navigation)
 */
const isInternalReferrer = (referrer: string): boolean => {
  if (!referrer) return false;
  
  try {
    const referrerDomain = new URL(referrer).hostname;
    const currentDomain = window.location.hostname;
    return referrerDomain === currentDomain;
  } catch {
    return false;
  }
};

/**
 * Clean domain for privacy-compliant storage
 * Removes subdomains and parameters that might contain sensitive info
 */
const cleanDomainForStorage = (domain: string): string => {
  // Remove common subdomains that don't add value
  const cleanDomain = domain.replace(/^(www\.|m\.|mobile\.|app\.)/, '');
  
  // For privacy, we could also limit to main domain + TLD
  // but keeping full domain for now as it's still privacy-compliant
  return cleanDomain;
};

/**
 * Categorize referrer source in a privacy-compliant way
 */
const categorizeReferrer = (domain: string): { category: ReferrerCategory; source?: string } => {
  const lowerDomain = domain.toLowerCase();
  
  // Check search engines
  for (const searchEngine of SEARCH_ENGINES) {
    if (lowerDomain.includes(searchEngine)) {
      return { 
        category: 'search_engine', 
        source: searchEngine.split('.')[0] // e.g., 'google', 'bing'
      };
    }
  }
  
  // Check social media
  for (const social of SOCIAL_MEDIA) {
    if (lowerDomain.includes(social)) {
      let sourceName = social.split('.')[0]; // e.g., 'twitter', 'linkedin'
      // Handle special cases
      if (sourceName === 'x') sourceName = 'twitter'; // Normalize x.com to twitter
      return { 
        category: 'social_media', 
        source: sourceName
      };
    }
  }
  
  // Check academic domains
  for (const academic of ACADEMIC_DOMAINS) {
    if (lowerDomain.includes(academic)) {
      let sourceName: string;
      if (academic.startsWith('.')) {
        sourceName = 'academic_institution';
      } else {
        sourceName = academic.split('.')[0];
      }
      return { 
        category: 'academic', 
        source: sourceName
      };
    }
  }
  
  // Check news/blog platforms
  for (const news of NEWS_BLOG) {
    if (lowerDomain.includes(news)) {
      return { 
        category: 'news_blog', 
        source: news.split('.')[0]
      };
    }
  }
  
  // Check email indicators
  for (const email of EMAIL_INDICATORS) {
    if (lowerDomain.includes(email)) {
      return { category: 'email' };
    }
  }
  
  // For other websites, store the cleaned domain as the source
  return { 
    category: 'other_website', 
    source: cleanDomainForStorage(domain)
  };
};

/**
 * Get privacy-compliant referrer information
 */
export const getReferrerInfo = (userAgent?: string): ReferrerInfo => {
  const ua = userAgent || navigator.userAgent;
  const lowerUA = ua.toLowerCase();
  
  // First check if it's a bot
  const botInfo = detectBot(ua);
  if (botInfo.isBot) {
    return {
      category: 'bot',
      source: botInfo.botName,
      isInternal: false,
      botType: botInfo.botType
    };
  }
  
  // Special case: Tesla browser
  if (lowerUA.includes('tesla')) {
    return {
      category: 'other_website',
      source: 'Tesla Browser',
      isInternal: false
    };
  }
  
  // Detect development tools (Cursor IDE, VSCode, etc.)
  for (const tool of DEV_TOOLS) {
    if (lowerUA.includes(tool)) {
      const toolName = tool.charAt(0).toUpperCase() + tool.slice(1);
      return {
        category: 'developer_tool',
        source: toolName,
        isInternal: false
      };
    }
  }
  
  const referrer = document.referrer;
  
  // No referrer = direct access (with Ecosia browser special case)
  if (!referrer) {
    // Ecosia browser with no referrer likely came from Ecosia search
    if (lowerUA.includes('ecosia')) {
      return {
        category: 'search_engine',
        source: 'ecosia',
        isInternal: false
      };
    }
    return { category: 'direct', isInternal: false };
  }
  
  // Check if internal navigation
  if (isInternalReferrer(referrer)) {
    return { category: 'direct', isInternal: true };
  }
  
  // Extract domain safely
  const domain = extractDomain(referrer);
  if (!domain) {
    return { category: 'unknown', isInternal: false };
  }
  
  // Categorize the referrer
  const { category, source } = categorizeReferrer(domain);
  
  return {
    category,
    source,
    isInternal: false
  };
};

/**
 * Extract UTM parameters from URL (for marketing campaigns)
 * Only extracts standard, non-sensitive UTM parameters
 */
export const getUTMParameters = (): Record<string, string> => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  // Standard UTM parameters - these are designed to be tracked
  const allowedUTMParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
  
  for (const param of allowedUTMParams) {
    const value = urlParams.get(param);
    if (value) {
      // Sanitize UTM values to prevent any potential data leakage
      utmParams[param] = value.substring(0, 100).replace(/[<>'"&]/g, '');
    }
  }
  
  return utmParams;
};
